import { PickupLocationForm } from "~/features/pickup-location-modal/ui/pickup-location.modal";
import { ConnectProto, LocationStatus } from "@racoon/api";
import type { Geofence } from "~/shared/lib/geofence";
import { splitAvailabilitySchedule } from "~/shared/lib/availability-schedule";

/**
 * Maps a gRPC PickupLocation to PickupLocationForm (form shape).
 */
export function formatPickupLocationToForm(
  location: ConnectProto.PickupLocation,
): PickupLocationForm {
  // Parse workFrom/workTo from availabilitySchedule (e.g. "09:00 - 20:00")
  let workFrom = "09:00";
  let workTo = "20:00";
  const [from, to] = splitAvailabilitySchedule(location.availabilitySchedule);
  if (from) workFrom = from;
  if (to) workTo = to;

  // Priority: numeric gRPC enum → form string
  let priority: "low" | "medium" | "high" = "medium";
  switch (location.priority) {
    case ConnectProto.PickupLocationPriority.HIGH:
      priority = "high";
      break;
    case ConnectProto.PickupLocationPriority.MEDIUM:
      priority = "medium";
      break;
    case ConnectProto.PickupLocationPriority.LOW:
      priority = "low";
      break;
    default:
      priority = "medium";
  }

  // Geofence mapping: geometry to Geofence[]
  let geofence: Geofence[] = [];
  // Supports a GeoJSON Feature wrapping either a Polygon
  // (coordinates: [[[lon, lat], ...]]) or a MultiPolygon
  // (coordinates: [[[[lon, lat], ...]], ...]), plus bare coordinates.
  if (location.geometry) {
    const geo: any =
      (location.geometry as any).geometry && (location.geometry as any).geometry.coordinates
        ? (location.geometry as any).geometry
        : location.geometry;
    let polygons: any[] = [];
    if (geo?.type === "MultiPolygon" && Array.isArray(geo.coordinates)) {
      polygons = geo.coordinates;
    } else if (Array.isArray(geo?.coordinates)) {
      polygons = [geo.coordinates];
    }
    geofence = polygons.flatMap((rings: any, index: number) => {
      const outerRing = Array.isArray(rings) ? rings[0] : null;
      if (!Array.isArray(outerRing)) return [];
      const latlngs = outerRing.map((pair: any) =>
        Array.isArray(pair) && pair.length === 2 ? [pair[1], pair[0]] : pair,
      );
      return [
        {
          id: location.id ? `${location.id}-${index}` : Math.random().toString(36).slice(2),
          latlngs,
        },
      ];
    });
  }

  // Status: numeric gRPC enum → string LocationStatus for form
  const statusToFormStatus: Partial<Record<ConnectProto.LocationStatus, LocationStatus>> = {
    [ConnectProto.LocationStatus.ACTIVE]: LocationStatus.LOCATION_STATUS_ACTIVE,
    [ConnectProto.LocationStatus.INACTIVE]: LocationStatus.LOCATION_STATUS_INACTIVE,
  };
  const formStatus = statusToFormStatus[location.status] ?? LocationStatus.LOCATION_STATUS_ACTIVE;

  const ownerOrg = (location as any).ownerOrganization;

  return {
    id: location.id ? String(location.id) : "",
    name: location.name ?? "",
    pickupLocationTypeId: String(location.pickupLocationTypeId ?? 0n),
    pickupLocationType: location.pickupLocationType,
    address: location.address ?? "",
    twoGisUrl: location.gisLink ?? "",
    is24h: false,
    workFrom,
    workTo,
    geozone: location.geoGroup ?? "1",
    tariff: Number(location.tariff ?? 4500),
    priority,
    bunkers: Number(location.containerGroups.length ?? 0),
    status: formStatus,
    latitude: location.lat?.toString() ?? "",
    longitude: location.lon?.toString() ?? "",
    geofence,
    clientId: ownerOrg?.id ? String(ownerOrg.id) : "",
    clientBin: ownerOrg?.bin ?? "",
    contractTerm: undefined,
    activeContractId: location.contractId ? String(location.contractId) : null,
    primary_contact_id: location.primaryContactId ? Number(location.primaryContactId) : undefined,
    contact_phone: "",
    contact_name: "",
  };
}
